import { interval, from, merge, of } from 'rxjs'
import {
  filter,
  map,
  switchMap,
  tap,
  distinctUntilChanged,
  shareReplay,
} from 'rxjs/operators'
import Actions from '../actions'
import { getPayload, catchAndDispatchError } from '../utils/rxUtils'
import {
  getCallToken,
  postCallStateUpdate,
  checkForPendingRooms,
} from '../utils/fetchUtils'
import config from '../config.json'
import { getOtherParticipants } from '../utils/appointmentsUtils'

const lastRoomKey = '@lastRoom'
const PUSH_NOTIFICATION_TYPE = Object.freeze({
  INCOMING_VIDEO_CALL: 'incomingVideoCall',
})

localStorage.removeItem(lastRoomKey)

export const checkForPendingCall = action$ => {
  const profile = action$.pipe(
    getPayload(Actions.USER_PROFILE_RECEIVED),
    map(({ profile }) => profile),
    shareReplay(1)
  )

  // When we get a video push we will go to the server in order to get the appointment details, we need it to understand
  // who is who in the appointment as each participant has its own location on the video call
  // Map back to profile for the userId
  const videoPush = action$.pipe(
    getPayload(Actions.PUSH_MESSAGE_RECEIVED),
    filter(
      ({ message }) =>
        message?.data?.type === PUSH_NOTIFICATION_TYPE.INCOMING_VIDEO_CALL
    ),
    switchMap(() => profile)
  )

  return merge(profile, videoPush).pipe(
    switchMap(({ id: userId }) => {
      return merge(
        interval(config.pollingIntervalInSeconds * 1000),
        of(1)
      ).pipe(map(() => ({ userId })))
    }),
    switchMap(({ userId }) => {
      const roomKey = localStorage.getItem(lastRoomKey)
      return (roomKey ? of(roomKey) : of({})).pipe(
        map(lastRoom => ({
          lastRoom,
          userId,
        }))
      )
    }),
    switchMap(({ userId, lastRoom }) => {
      return checkForPendingRooms(userId).pipe(
        map(({ room, appointment, sessionName }) => ({
          roomName: room || sessionName,
          isZoom: !!sessionName,
          appointment,
          userId,
        })),
        filter(({ roomName }) => roomName !== lastRoom),
        catchAndDispatchError
      )
    }),
    distinctUntilChanged((a, b) => a.roomName === b.roomName),
    filter(({ roomName }) => roomName),
    switchMap(({ roomName, appointment, userId, isZoom }) => {
      return getCallToken(userId, roomName).pipe(
        switchMap(accessToken => {
          const data = {
            roomName,
            appointment,
            accessToken,
            isZoom,
          }

          // Once the user enters the room, we don't want to re-enter him in case he closed the app
          // or hit disconnect, so save it for checking. This needs to be checked only once, since as long as the app
          // is open the distinict until changed will protect from double entering the room
          localStorage.setItem(lastRoomKey, roomName)
          return from([
            Actions.createAction(Actions.VIDEO_CALL_DATA_RECEIVED, {
              data,
              userId,
            }),
            Actions.createAction(Actions.VIDEO_CALL_INITED, { data }),
            Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
              eventName: Actions.VIDEO_CALL_INITED + '_WEB',
            }),
          ])
        }),
        catchAndDispatchError
      )
    })
  )
}

export const updateCallState = action$ => {
  return action$.pipe(
    getPayload(Actions.VIDEO_CALL_DATA_RECEIVED),
    tap(({ userId }) => postCallStateUpdate(userId)),
    filter(_ => false) // do not emit anything
  )
}

export const incomingVideoCall = action$ => {
  // We could listen to video_call_inited, but since it comes
  // after a couple api calls, i would rather first put the user in the waiting room
  // and then fetch
  return action$.pipe(
    getPayload(Actions.VIDEO_CALL_DATA_RECEIVED),
    switchMap(({ data, userId }) =>
      of(
        Actions.createAction(Actions.JOIN_VIDEO_RESOLVED, 'approved'),
        Actions.createAction(
          Actions.GET_OTHER_PARTICIPANT,
          getOtherParticipants(data, userId)
        )
      )
    )
  )
}
